import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, ChevronRight, DoorOpen, Wrench, Settings, Key, Phone } from "lucide-react";

const doorServices = [
  {
    id: 1,
    name: "Door Hanging",
    description: "New internal doors hung properly. I'll fit your door so it opens, closes and latches correctly every time.",
    icon: DoorOpen,
    features: ["Proper hinge alignment", "Correct gap all round", "Smooth operation", "Latch lines up"]
  },
  {
    id: 2,
    name: "Door Adjustments",
    description: "Doors sticking, catching or not closing properly? I can adjust, plane or rehang doors to get them working right.",
    icon: Settings,
    features: ["Sticking doors fixed", "Dropped hinges adjusted", "Planing where needed", "Latch problems sorted"]
  },
  {
    id: 3,
    name: "Door Furniture",
    description: "Handles, locks, latches and hinges fitted. Whether you're replacing old hardware or fitting new, I'll get it done properly.",
    icon: Key,
    features: ["Handles & knobs", "Locks & latches", "Hinges replaced", "Letterboxes fitted"]
  },
  {
    id: 4,
    name: "Fire Doors",
    description: "Fire door installation and upgrades. Fitted to comply with regulations, with the correct intumescent strips and closers.",
    icon: Wrench,
    features: ["Proper certification", "Intumescent strips", "Door closers", "Regulations met"]
  }
];

const DoorsPage = () => {
  return (
    <div>
      <Helmet>
        <title>Door Hanging & Fitting | Internal Doors | Expert Carpenter Greater Manchester</title>
        <meta name="description" content="Professional door hanging and fitting in Greater Manchester. Internal doors that fit and close properly. Door adjustments, repairs and hardware. Call 07300 405045 for a free quote." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/doors" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How long does it take to hang a door?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A straightforward door hang typically takes 1-2 hours. If the frame needs adjusting or it's a fire door with intumescent strips, it may take a bit longer."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need to supply the door?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can supply your own door, or I can pick one up for you. I'm happy to advise on sizes and styles if needed."
                }
              },
              {
                "@type": "Question",
                "name": "Can you fix a door that's sticking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes - sticking doors are usually a quick fix. It might just need planing down, or the hinges may need adjusting. I'll take a look and let you know."
                }
              },
              {
                "@type": "Question",
                "name": "Do you fit door handles and locks?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, I fit all door furniture including handles, latches, locks, and hinges. Just let me know what you need when you get in touch."
                }
              },
              {
                "@type": "Question",
                "name": "What about fire doors?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I fit fire doors to the correct standards with the right intumescent strips and smoke seals. I can also fit door closers if required."
                }
              },
              {
                "@type": "Question",
                "name": "Do you tidy up afterwards?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Always. I clean up as I go and leave the area tidy. You won't be left with sawdust everywhere."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm md:text-base">Main Service</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Door Hanging & Fitting
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Internal doors hung properly. Doors that open, close and latch as they should.
                No sticking, no catching, no gaps where there shouldn't be.
              </p>
              
              <ul className="space-y-4 mb-8 text-left max-w-md mx-auto lg:mx-0">
                {[
                  "Doors that fit and close properly",
                  "Neat, consistent gaps",
                  "Hinges aligned correctly",
                  "Latches that work first time"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-200 text-base md:text-lg">
                    <div className="w-7 h-7 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-[#C9A227]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-col gap-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-lg h-14 px-8 w-full" data-testid="doors-get-quote-btn">
                    Get a Free Quote
                    <ChevronRight className="ml-2 h-6 w-6" />
                  </Button>
                </Link>
                <a href="tel:07300405045" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg h-14 w-full">
                    <Phone className="mr-2 h-6 w-6" />
                    07300 405045
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_panache-review/artifacts/w9aafbvk_carpenter%20door.jpg"
                  alt="Carpenter hanging an internal door"
                  className="w-full h-[550px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-14 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">What I Do</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3A3A3A]">
              Door Services
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              From hanging new doors to sorting out ones that don't work properly.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {doorServices.map((service) => (
              <Card key={service.id} className="group p-5 md:p-8 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white">
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="p-3 md:p-4 bg-[#2D5A4A] rounded-xl group-hover:bg-[#C9A227] transition-colors flex-shrink-0">
                    <service.icon className="h-6 w-6 md:h-7 md:w-7 text-white group-hover:text-black transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-[#3A3A3A] mb-2 md:mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm md:text-base">{service.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, i) => (
                        <span
                          key={i}
                          className="text-xs bg-[#f8f7f5] text-[#2D5A4A] px-3 py-1.5 rounded-full font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          <div className="mt-10 md:mt-16 text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#2D5A4A] hover:bg-[#234839] h-14 px-10 text-lg font-bold w-full sm:w-auto">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How I Work */}
      <section className="py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">How I Work</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A3A3A] mb-4">
              Door Hanging Done Properly
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              Every door I hang follows the same careful process.
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              { title: "Check the frame", desc: "I check the frame is square and make any adjustments needed before hanging the door." },
              { title: "Mark & cut hinges", desc: "Hinges are marked accurately and cut in cleanly so the door sits properly in the frame." },
              { title: "Fit the door", desc: "Door is hung with even gaps all round - typically 2-3mm at the top and sides, slightly more at the bottom." },
              { title: "Test & adjust", desc: "I'll open and close the door multiple times, making fine adjustments until it works perfectly." },
              { title: "Fit the latch", desc: "Latch or lock fitted so it engages smoothly and the door closes securely." }
            ].map((step, i) => (
              <div key={i} className="flex gap-4 items-start bg-white p-5 rounded-xl shadow-sm">
                <div className="w-10 h-10 bg-[#2D5A4A] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-bold text-[#3A3A3A] mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-10 sm:py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">Common Questions</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3A3A3A]">
              Door Fitting FAQs
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              { 
                q: "How long does it take to hang a door?", 
                a: "A straightforward door hang typically takes 1-2 hours. If the frame needs adjusting or it's a fire door with intumescent strips, it may take a bit longer." 
              },
              { 
                q: "Do I need to supply the door?", 
                a: "You can supply your own door, or I can pick one up for you. I'm happy to advise on sizes and styles if needed." 
              },
              { 
                q: "Can you fix a door that's sticking?", 
                a: "Yes - sticking doors are usually a quick fix. It might just need planing down, or the hinges may need adjusting. I'll take a look and let you know." 
              },
              { 
                q: "Do you fit door handles and locks?", 
                a: "Yes, I fit all door furniture including handles, latches, locks, and hinges. Just let me know what you need when you get in touch." 
              },
              { 
                q: "What about fire doors?", 
                a: "I fit fire doors to the correct standards with the right intumescent strips and smoke seals. I can also fit door closers if required." 
              },
              { 
                q: "Do you tidy up afterwards?", 
                a: "Always. I clean up as I go and leave the area tidy. You won't be left with sawdust everywhere." 
              }
            ].map((faq, i) => (
              <div key={i} className="bg-[#f8f7f5] p-4 sm:p-5 rounded-xl">
                <h3 className="font-bold text-[#3A3A3A] mb-2 text-sm sm:text-base">{faq.q}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-28 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5">
            Need Doors Hung?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8">
            Get in touch and I'll give you a price. Simple jobs quoted over the phone, bigger jobs I'll come and take a look.
          </p>
          <div className="flex flex-col gap-4">
            <Link to="/contact" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-14 px-10 text-lg w-full">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:07300405045" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold h-14 px-10 text-lg w-full">
                <Phone className="mr-2 h-6 w-6" />
                Call 07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DoorsPage;
